import { c as _c } from "react/compiler-runtime";
import type { ElementType } from 'react';
import styles from './Tabs.module.css';
import type { TabContentProps, TabListProps, TabProps, TabsProps } from './types';
import classNames from 'classnames';
import { Icon } from '../Icon';
import { LayoutCluster, LayoutStack } from '../layouts';
import useTabsKeyboardNavigation from './useTabsKeyboardNavigation';
function Tabs(t0) {
  const $ = _c(6);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children || $[4] !== props) {
    t1 = <LayoutStack space="400" {...props}>{children}</LayoutStack>;
    $[3] = children;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function Tab<T extends ElementType = 'button'>({
  as,
  children,
  isActive = false,
  id,
  iconName,
  className,
  ...props
}: TabProps<T>) {
  const Tag = as ?? 'button';
  return <Tag role="tab" aria-selected={isActive} aria-controls={`panel-${id}`} className={classNames(styles.tab, className, {
    [styles.activeTab!]: isActive
  })}
  // Roving tabIndex: only active item is tabbable directly
  tabIndex={isActive ? 0 : -1} id={`tab-${id}`} {...props}>
      <Icon name={iconName} size="300" />
      <span className={styles.label}>
        <span className={styles.labelVisible}>{children}</span>
        {/* Setting the size of the element based on the bold size  */}
        <span aria-hidden="true" className={styles.labelGhost}>
          {children}
        </span>
      </span>
    </Tag>;
}

// aria-labelledby => should point to the label for the tabs
export function TabList(t0) {
  const $ = _c(15);
  let children;
  let className;
  let onKeyDown;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      onKeyDown,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onKeyDown;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    onKeyDown = $[3];
    props = $[4];
  }
  const {
    onKeyDown: onKeyDown_
  } = useTabsKeyboardNavigation();
  let t1;
  if ($[5] !== className) {
    t1 = classNames(styles.tabList, className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== onKeyDown || $[8] !== onKeyDown_) {
    t2 = e => {
      (onKeyDown as ((event: React.KeyboardEvent<HTMLElement>) => void) | undefined)?.(e);
      onKeyDown_(e);
    };
    $[7] = onKeyDown;
    $[8] = onKeyDown_;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== children || $[11] !== props || $[12] !== t1 || $[13] !== t2) {
    t3 = <LayoutCluster {...props} role="tablist" className={t1} onKeyDown={t2}>{children}</LayoutCluster>;
    $[10] = children;
    $[11] = props;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
}
export function TabPanel(t0) {
  const $ = _c(9);
  let activeTabId;
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      activeTabId,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = activeTabId;
    $[2] = children;
    $[3] = props;
  } else {
    activeTabId = $[1];
    children = $[2];
    props = $[3];
  }
  if (!activeTabId) {
    return null;
  }
  const t1 = `tab-${activeTabId}`;
  const t2 = `panel-${activeTabId}`;
  let t3;
  if ($[4] !== children || $[5] !== props || $[6] !== t1 || $[7] !== t2) {
    t3 = <div {...props} role="tabpanel" tabIndex={0} aria-labelledby={t1} id={t2}>{children}</div>;
    $[4] = children;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
Tabs.TabTrigger = Tab;
Tabs.TabList = TabList;
Tabs.TabPanel = TabPanel;
export default Tabs;